import React, { useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, LoadingIndicator } from '@ygtechnologies/bygr-frontend-component-library';
import { Trans } from '@lingui/react/macro';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { getOrder, getRelatedOrders, setOrderResponsibles } from '@/services/orders';
import { getProjectProfiles } from '@/services/projects';
import { Button } from '@/components/Buttons';
import { Order, ProjectProfile } from '@/types';
import OrderResponsibleSelection from './OrderResponsibleSelection';
import OrderResponsibleOrderSelection from './OrderResponsibleOrderSelection';

interface OrderChangeResponsibleDialogProps {
    orderId: number;
    projectId: number;
    show: boolean;
    onAccept(orderIds: number[], newResponsible: ProjectProfile | null): void;
    onClose(): void;
}

enum Step {
    User,
    Orders,
}

const stepOrder = [Step.User, Step.Orders];
const firstStep = stepOrder[0];
const lastStep = stepOrder[stepOrder.length - 1];

const OrderChangeResponsibleDialog: React.FC<OrderChangeResponsibleDialogProps> = ({
    projectId,
    orderId,
    show,
    onAccept,
    onClose,
}) => {
    const [step, setStep] = useState<Step>(firstStep);
    const [saving, setSaving] = useState(false);
    const [members, setMembers] = useState<ProjectProfile[] | null>(null);
    const [chosenMember, setChosenMember] = useState<ProjectProfile | null>(null);
    const [selectedOrderIds, setSelectedOrderIds] = useState<number[]>([]);
    const [relatedOrders, setRelatedOrders] = useState<Order[] | null>(null);
    const [isChangeRequest, setIsChangeRequest] = useState<boolean>(false);

    const moveStep = (delta) => {
        setStep(stepOrder[stepOrder.indexOf(step) + delta]);
    };

    const handleOpen = async () => {
        setSaving(false);
        setChosenMember(null);
        setStep(firstStep);

        const [order, members, relatedOrders] = await Promise.all([
            getOrder(orderId),
            getProjectProfiles(projectId),
            getRelatedOrders(orderId),
        ]);

        setMembers(members);
        setRelatedOrders(relatedOrders);

        const chosenMember = members.find((x) => x.profileId === order.responsibleProfileId);

        if (chosenMember) {
            setChosenMember(chosenMember);
        }
        setIsChangeRequest(!!order.changeRequestId);
        setSelectedOrderIds([order.id]);
    };

    const handleSaveClick = async () => {
        setSaving(true);

        try {
            await setOrderResponsibles(selectedOrderIds, chosenMember ? chosenMember.profileId : null);

            triggerHandler(onAccept, selectedOrderIds, chosenMember ? chosenMember : null);
            triggerHandler(onClose);
        } finally {
            setSaving(false);
        }
    };

    const handleCancelClick = () => {
        triggerHandler(onClose);
    };

    const renderContent = () => {
        if (!members) {
            return <LoadingIndicator />;
        }

        if (step === Step.User) {
            return <OrderResponsibleSelection members={members} value={chosenMember} onChange={setChosenMember} />;
        }

        if (step === Step.Orders) {
            return (
                <OrderResponsibleOrderSelection
                    orders={relatedOrders!}
                    originalOrderId={orderId}
                    onChange={setSelectedOrderIds}
                />
            );
        }

        return null;
    };

    return (
        <Modal isOpen={show} toggle={handleCancelClick} onOpen={handleOpen}>
            <ModalHeader>
                <Trans>Set responsible</Trans>
            </ModalHeader>
            <ModalBody>{renderContent()}</ModalBody>
            <ModalFooter>
                <Button color="tertiary" onClick={handleCancelClick} disabled={saving}>
                    <Trans>Cancel</Trans>
                </Button>

                {step !== firstStep && (
                    <Button color="secondary" onClick={(e) => moveStep(-1)}>
                        <Trans>Back</Trans>
                    </Button>
                )}

                {step !== lastStep && !isChangeRequest && (
                    <Button
                        color="primary"
                        // disabled={chosenMember === null}
                        onClick={(e) => moveStep(+1)}>
                        <Trans>Next</Trans>
                    </Button>
                )}

                {(step === lastStep || isChangeRequest) && (
                    <Button color="primary" onClick={handleSaveClick} loading={saving}>
                        <Trans>Save</Trans>
                    </Button>
                )}
            </ModalFooter>
        </Modal>
    );
};

export default OrderChangeResponsibleDialog;